import { useEffect, useState } from "react";
import axios from "axios";
import { Confetti, ChartBar, Trophy, ShareNetwork, X, Download } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * WeeklyRecapCard
 *
 * Compact Sunday/Monday "your week in lifts" surface on the Dashboard. Hits
 * GET /api/recap/weekly to grab the summary, and opens a full-screen share
 * modal showing the 1080×1920 PNG (rendered by the backend) with native
 * Web Share, "Save image", and "Hide" actions.
 *
 * Controlled by `profile.dashboard_recap_optin` (defaults true). When the
 * user toggles it off in Settings the card disappears.
 *
 * Also self-hides for the week if the user explicitly dismisses it (per-week
 * localStorage key).
 */
export default function WeeklyRecapCard({ api, profile }) {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [pngUrl, setPngUrl] = useState(null);
  const [pngLoading, setPngLoading] = useState(false);

  const enabled = !profile || profile.dashboard_recap_optin !== false;

  // Per-week dismissal — once the user "Hide for now" we don't show until next week.
  const dismissKey = recap ? `uptop-recap-dismissed-${recap.week_start}` : null;
  const dismissed = dismissKey ? localStorage.getItem(dismissKey) === "1" : false;

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setLoading(false);
      return () => {};
    }
    (async () => {
      try {
        const r = await axios.get(`${api}/recap/weekly?week_offset=0`);
        if (!cancelled) setRecap(r.data);
      } catch (e) {
        if (!cancelled) setRecap(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, enabled]);

  if (!enabled || loading) return null;
  if (!recap || !recap.has_activity || dismissed) return null;

  const totals = recap.totals || {};
  const newPRs = recap.new_prs?.length || 0;
  const volumeStr = totals.volume >= 1000
    ? `${(totals.volume / 1000).toFixed(1)}t`
    : `${Math.round(totals.volume || 0)}kg`;

  const openShare = async () => {
    setShareOpen(true);
    setPngLoading(true);
    try {
      const res = await axios.get(`${api}/recap/weekly.png?week_offset=0`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setPngUrl(url);
    } catch (e) {
      toast.error("Couldn't load recap image. Try again in a moment.");
      setShareOpen(false);
    } finally {
      setPngLoading(false);
    }
  };

  const closeShare = () => {
    setShareOpen(false);
    if (pngUrl) {
      URL.revokeObjectURL(pngUrl);
      setPngUrl(null);
    }
  };

  const handleNativeShare = async () => {
    if (!pngUrl) return;
    try {
      const blob = await (await fetch(pngUrl)).blob();
      const file = new File([blob], "uptop-week-recap.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My week on UpTop",
          text: "My week in lifts on UpTop — uptopapp.com",
        });
      } else {
        // Fallback: trigger download
        const a = document.createElement("a");
        a.href = pngUrl;
        a.download = "uptop-week-recap.png";
        a.click();
        toast.success("Image downloaded — share it from your photos.");
      }
    } catch (e) {
      // User likely cancelled the share sheet — silent
    }
  };

  const handleDownload = () => {
    if (!pngUrl) return;
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = "uptop-week-recap.png";
    a.click();
  };

  const handleHideThisWeek = () => {
    if (dismissKey) localStorage.setItem(dismissKey, "1");
    closeShare();
    // Trigger re-render by manually toggling state
    setRecap({ ...recap });
  };

  return (
    <>
      <div
        className="relative bg-gradient-to-br from-[#E05A1D]/15 via-[#E05A1D]/5 to-transparent border border-[#E05A1D]/40 p-5 overflow-hidden"
        data-testid="weekly-recap-card"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center border border-[#E05A1D]/50 bg-[#E05A1D]/15 text-[#E05A1D]">
              <Confetti size={16} weight="fill" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E05A1D]">
                Your Week in Lifts
              </p>
              <p
                className="text-base font-bold tracking-tight uppercase text-white leading-tight"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {formatRange(recap.week_start, recap.week_end)}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <RecapStat label="Workouts" value={totals.workouts || 0} />
          <RecapStat label="Volume" value={volumeStr} />
          <RecapStat label="PRs" value={newPRs} accent={newPRs > 0} />
        </div>

        <div className="flex gap-2">
          <button
            onClick={openShare}
            className="flex-1 bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-2.5 text-xs flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
            data-testid="weekly-recap-share-btn"
          >
            <ShareNetwork size={14} weight="bold" />
            Share my week
          </button>
          <button
            onClick={handleHideThisWeek}
            className="border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#3f3f46] font-bold uppercase tracking-wide py-2.5 px-4 text-xs transition-colors"
            data-testid="weekly-recap-hide-btn"
            title="Hide until next week"
          >
            Hide
          </button>
        </div>
      </div>

      {shareOpen && (
        <div
          className="fixed inset-0 z-[120] bg-black/85 flex items-center justify-center px-4 py-6 overflow-y-auto"
          data-testid="weekly-recap-share-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md relative">
            <button
              onClick={closeShare}
              className="absolute -top-12 right-0 text-white/70 hover:text-white"
              data-testid="weekly-recap-close-modal"
              aria-label="Close"
            >
              <X size={24} weight="bold" />
            </button>
            {pngLoading || !pngUrl ? (
              <div className="aspect-[9/16] bg-[#161616] border border-[#27272A] flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-[#E05A1D] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <img
                src={pngUrl}
                alt="Your week in lifts"
                className="w-full block border border-[#27272A] shadow-2xl"
                data-testid="weekly-recap-png"
              />
            )}
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button
                onClick={handleNativeShare}
                disabled={!pngUrl}
                className="bg-[#E05A1D] hover:bg-[#F06B2E] disabled:opacity-30 text-white font-bold uppercase tracking-wide py-3 text-xs flex items-center justify-center gap-2 transition-colors"
                data-testid="weekly-recap-share-native-btn"
              >
                <ShareNetwork size={14} weight="bold" />
                Share
              </button>
              <button
                onClick={handleDownload}
                disabled={!pngUrl}
                className="border border-[#27272A] hover:border-[#3f3f46] text-white font-bold uppercase tracking-wide py-3 text-xs flex items-center justify-center gap-2 transition-colors disabled:opacity-30"
                data-testid="weekly-recap-download-btn"
              >
                <Download size={14} weight="bold" />
                Save image
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RecapStat({ label, value, accent }) {
  return (
    <div className="bg-black/30 border border-[#27272A] p-3 text-center">
      <p className="text-[10px] font-bold uppercase tracking-wide text-[#71717A] mb-1">{label}</p>
      <p
        className={`text-2xl font-black ${accent ? "text-[#E05A1D]" : "text-white"}`}
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
      >
        {value}
      </p>
    </div>
  );
}

function formatRange(start, end) {
  try {
    const [sy, sm, sd] = start.split("-");
    const [ey, em, ed] = end.split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (sm === em) return `${months[+sm - 1]} ${+sd}–${+ed}`;
    return `${months[+sm - 1]} ${+sd} – ${months[+em - 1]} ${+ed}`;
  } catch (e) {
    return `${start} → ${end}`;
  }
}
